/**
 * Bajo y Respuesta — rhythm-03-pim
 * Frases con contraste (pregunta/respuesta, bajo caminante, células mixtas).
 */
import type { RhythmSubMission, RhythmTargetNote } from "./rhythmPathPlan";
import { att, makeStep, rst } from "./rhythmPatternBuilders";

const step = (
  partial: Parameters<typeof makeStep>[1]
): RhythmSubMission => makeStep("rhythm-03-pim", partial);

const barPimi = (bass: string): RhythmTargetNote[] => [
  att(bass, 1, "p"),
  att("E4", 1, "i"),
  att("B3", 1, "m"),
  att("E4", 1, "i"),
];

const barPimiAlt = (bass: string): RhythmTargetNote[] => [
  att(bass, 1, "p"),
  att("B3", 1, "m"),
  att("E4", 1, "i"),
  att("B3", 1, "m"),
];

const barPBlancaFill = (bass: string): RhythmTargetNote[] => [
  att(bass, 2, "p"),
  att("E4", 0.5, "i"),
  att("B3", 0.5, "m"),
  att("E4", 0.5, "i"),
  att("B3", 0.5, "m"),
];

export const RHYTHM_03_PIM: RhythmSubMission[] = [
  step({
    id: "pim-3-1-dialogo",
    title: "El diálogo",
    subtitle: "Blanca p · respuesta i (varía)",
    blockLabel: "Bloque 1 · Pregunta y respuesta",
    kind: "corto",
    goal: "Pregunta grave y respuesta aguda con dos formas.",
    targetPattern: [
      // Forma A: blanca–blanca
      att("E2", 2, "p"),
      att("E4", 2, "i"),
      att("E2", 2, "p"),
      att("E4", 2, "i"),
      // Forma B: blanca + dos negras
      att("E2", 2, "p"),
      att("E4", 1, "i"),
      att("E4", 1, "i"),
      att("E2", 2, "p"),
      att("E4", 1, "i"),
      att("E4", 1, "i"),
    ],
    instructions: [
      "Primero: blanca de pulgar (6ª) y blanca de índice (1ª), dos veces.",
      "Luego: blanca de pulgar y dos negras de índice en la 1ª, dos veces.",
      "Misma pregunta; la respuesta cambia de figura.",
    ],
    tip: "El pulgar pregunta; el índice contesta. No hablen a la vez.",
    feedbackOk: "¡Diálogo con dos respuestas!",
    feedbackError: "Forma A (blanca–blanca) y forma B (blanca + 2 negras).",
  }),
  step({
    id: "pim-3-2-dialogo-inv",
    title: "Diálogo invertido",
    subtitle: "Blanca p · respuesta m (varía)",
    blockLabel: "Bloque 1 · Pregunta y respuesta",
    kind: "corto",
    goal: "Medio responde; dos formas de contestar.",
    targetPattern: [
      att("A2", 2, "p"),
      att("B3", 2, "m"),
      att("A2", 2, "p"),
      att("B3", 2, "m"),
      att("A2", 2, "p"),
      att("B3", 1, "m"),
      att("B3", 1, "m"),
      att("A2", 2, "p"),
      att("B3", 1, "m"),
      rst(1),
    ],
    instructions: [
      "Bajo en 5ª; responde el Medio en 2ª.",
      "Dos veces blanca–blanca; luego blanca + dos negras de Medio.",
      "El último ciclo cierra con silencio: no ataques ahí.",
    ],
    tip: "Invertir roles entrena la mano completa, no solo el índice.",
    feedbackOk: "¡Diálogo con el Medio!",
    feedbackError: "p en 5ª y m en 2ª, con las dos formas y el silencio final.",
  }),
  step({
    id: "pim-3-3-relleno",
    title: "El relleno",
    subtitle: "Tres células de relleno",
    blockLabel: "Bloque 1 · Pregunta y respuesta",
    kind: "medio",
    goal: "El bajo manda; el relleno cambia de orden.",
    targetPattern: [
      // p–i–m–i
      ...barPimi("E2"),
      ...barPimi("E2"),
      // p–m–i–m
      ...barPimiAlt("E2"),
      ...barPimiAlt("E2"),
      // vuelta al base
      ...barPimi("E2"),
      ...barPimi("E2"),
    ],
    instructions: [
      "Dos compases p–i–m–i (relleno clásico).",
      "Dos compases p–m–i–m (empieza el Medio).",
      "Vuelves a p–i–m–i. El bajo sigue en 6ª.",
    ],
    tip: "Cambiar el orden del relleno obliga a escuchar, no a memorizar un loop.",
    feedbackOk: "¡Relleno con variación!",
    feedbackError: "Dos de p–i–m–i, dos de p–m–i–m, dos de p–i–m–i.",
  }),
  step({
    id: "pim-3-4-resistencia-i",
    title: "Resistencia I",
    subtitle: "Secciones · p–i–m–i y contraste",
    blockLabel: "Bloque 1 · Pregunta y respuesta",
    kind: "resistencia",
    isEndurance: true,
    goal: "Motor estable con capítulos, no 16 clones.",
    targetPattern: [
      ...Array.from({ length: 4 }, () => barPimi("E2")).flat(),
      ...Array.from({ length: 4 }, () => barPBlancaFill("E2")).flat(),
      ...Array.from({ length: 4 }, () => barPimiAlt("E2")).flat(),
      ...Array.from({ length: 4 }, () => barPimi("E2")).flat(),
    ],
    instructions: [
      "Capítulo 1: cuatro compases p–i–m–i.",
      "Capítulo 2: blanca de bajo + 4 corcheas de respuesta.",
      "Capítulo 3: p–m–i–m. Capítulo 4: vuelve a p–i–m–i.",
    ],
    tip: "Cada capítulo pide otra intención. Relaja la mandíbula entre ellos.",
    feedbackOk: "Motor con forma. Bloque 1 listo.",
    feedbackError: "Cuatro capítulos distintos. No adelantes el pulgar.",
  }),

  step({
    id: "pim-3-5-caminante",
    title: "Bajo caminante",
    subtitle: "Mapa de bajos · no solo 6–5–6–5",
    blockLabel: "Bloque 2 · Bajos alternados",
    kind: "corto",
    goal: "El bajo camina con frases, no un metrónomo de dos notas.",
    targetPattern: [
      // 6 6 5 5
      ...barPimi("E2"),
      ...barPimi("E2"),
      ...barPimi("A2"),
      ...barPimi("A2"),
      // 5 5 6 6 (espejo)
      ...barPimi("A2"),
      ...barPimi("A2"),
      ...barPimi("E2"),
      ...barPimi("E2"),
      // 6 5 6 5
      ...barPimi("E2"),
      ...barPimi("A2"),
      ...barPimi("E2"),
      ...barPimi("A2"),
    ],
    instructions: [
      "Primero: dos compases en 6ª y dos en 5ª.",
      "Luego el espejo: dos en 5ª y dos en 6ª.",
      "Cierra alternando 6ª–5ª–6ª–5ª.",
    ],
    tip: "El bajo caminante prepara progresiones: escucha el mapa, no el loop.",
    feedbackOk: "¡Bajo con mapa!",
    feedbackError: "Bloques 66-55, espejo 55-66, y alterno 6-5.",
  }),
  step({
    id: "pim-3-6-respuesta-rapida",
    title: "Respuesta rápida",
    subtitle: "Blanca + corcheas · dos bajos",
    blockLabel: "Bloque 2 · Bajos alternados",
    kind: "corto",
    goal: "Contraste lento/rápido con cambio de bajo.",
    targetPattern: [
      ...barPBlancaFill("E2"),
      ...barPBlancaFill("E2"),
      ...barPBlancaFill("A2"),
      ...barPBlancaFill("A2"),
      // Cierre: negra de bajo + relleno corto
      att("E2", 1, "p"),
      att("E4", 1, "i"),
      att("B3", 1, "m"),
      att("E4", 1, "i"),
      att("A2", 1, "p"),
      att("E4", 1, "i"),
      att("B3", 1, "m"),
      att("E4", 1, "i"),
    ],
    instructions: [
      "Dos veces: blanca de bajo en 6ª + 4 corcheas.",
      "Dos veces igual con bajo en 5ª.",
      "Cierra con dos compases p–i–m–i (6ª y 5ª).",
    ],
    tip: "Tras la blanca, no te adelantes a las corcheas: siente el click mitad.",
    feedbackOk: "¡Respuesta rápida con mapa de bajos!",
    feedbackError: "Blanca+corcheas en 6ª y 5ª, luego cierre p–i–m–i.",
  }),
  step({
    id: "pim-3-7-integracion",
    title: "Integración total",
    subtitle: "Suite corta · tres escenas",
    blockLabel: "Bloque 2 · Bajos alternados",
    kind: "medio",
    goal: "Una mini-pieza: caminata, contraste, cierre.",
    targetPattern: [
      ...barPimi("E2"),
      ...barPimi("A2"),
      ...barPBlancaFill("E2"),
      ...barPimiAlt("A2"),
      ...barPimi("E2"),
      ...barPimi("A2"),
      ...barPBlancaFill("A2"),
      ...barPimi("E2"),
    ],
    instructions: [
      "Escena 1: p–i–m–i en 6ª y 5ª.",
      "Escena 2: blanca+corcheas en 6ª, luego p–m–i–m en 5ª.",
      "Escena 3: vuelve a caminar y cierra con blanca+corcheas en 5ª + p–i–m–i en 6ª.",
    ],
    tip: "Lee la timeline como partitura de tres escenas, no como loop.",
    feedbackOk: "¡Integración con forma!",
    feedbackError: "Sigue las tres escenas en orden.",
  }),
  step({
    id: "pim-3-8-resistencia-ii",
    title: "Resistencia II",
    subtitle: "Cuatro capítulos · motor continuo",
    blockLabel: "Bloque 2 · Bajos alternados",
    kind: "resistencia",
    isEndurance: true,
    goal: "Acompañamiento largo con secciones contrastadas.",
    targetPattern: [
      // Cap 1: caminata 6-5
      ...Array.from({ length: 6 }, (_, i) =>
        barPimi(i % 2 === 0 ? "E2" : "A2")
      ).flat(),
      // Cap 2: bloques 6×2 / 5×2
      ...barPimi("E2"),
      ...barPimi("E2"),
      ...barPimi("A2"),
      ...barPimi("A2"),
      ...barPimi("E2"),
      ...barPimi("E2"),
      // Cap 3: contraste rápido
      ...Array.from({ length: 4 }, () => barPBlancaFill("E2")).flat(),
      ...Array.from({ length: 4 }, () => barPBlancaFill("A2")).flat(),
      // Cap 4: vuelta + alt
      ...Array.from({ length: 4 }, () => barPimi("E2")).flat(),
      ...Array.from({ length: 4 }, () => barPimiAlt("A2")).flat(),
    ],
    instructions: [
      "Capítulo 1: caminata 6–5. Capítulo 2: bloques de dos compases por bajo.",
      "Capítulo 3: blanca + corcheas. Capítulo 4: p–i–m–i y p–m–i–m.",
      "No pares entre capítulos: cambia la intención, no el pulso.",
    ],
    tip: "Cuando esto fluye, ya estás a un paso de la fogata.",
    feedbackOk: "Motor continuo con forma. Misión lista.",
    feedbackError: "Cuatro capítulos. Recupera siempre en el tiempo 1 (pulgar).",
  }),
];
